import { CartAddEvent } from '@theme/events';

/**
 * <knott-bundle> — Knott gift sets and "frequently bought together".
 *
 * Reads its setup from a JSON <script data-kb-config> child:
 *   rule: "slots"  → each piece has its own saving (percent or amount)
 *         "stack"  → 3 pieces: cheapest is free; 2 pieces: cheaper one gets pair_percent off
 *   slots: [{ label, percent, amount (cents), optional, locked, products: [...] }]
 *
 * The prices shown here mirror the automatic discounts set up in Shopify,
 * which are what actually apply at checkout.
 */
class KnottBundle extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('script[data-kb-config]');
    if (!raw) return;
    try {
      this.config = JSON.parse(raw.textContent || '{}');
    } catch (e) {
      return;
    }
    this.slots = (this.config.slots || []).filter((s) => s.products && s.products.length);
    this.state = this.slots.map((slot) => {
      const product = slot.products[0];
      return { p: 0, v: this.firstVariant(product), on: true };
    });
    this.dialog = this.querySelector('dialog');
    this.renderRows();
    this.update();

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
  }

  firstVariant(product) {
    const ok = product.variants.find((v) => v.id === product.first && v.available);
    return (ok || product.variants.find((v) => v.available) || product.variants[0]).id;
  }

  money(cents) {
    const format = this.config.money_format || '${{amount}}';
    const value = Math.max(0, Math.round(cents)) / 100;
    const withDecimals = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withDecimals.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, withDecimals);
  }

  esc(text) {
    const d = document.createElement('div');
    d.textContent = text == null ? '' : String(text);
    return d.innerHTML;
  }

  current(i) {
    const slot = this.slots[i];
    const st = this.state[i];
    const product = slot.products[st.p];
    const variant = product.variants.find((v) => v.id === st.v) || product.variants[0];
    return { slot, st, product, variant };
  }

  renderRows() {
    const box = this.querySelector('[data-kb-rows]');
    if (!box) return;
    box.innerHTML = this.slots
      .map((slot, i) => {
        const { st, product } = this.current(i);
        const productSelect =
          slot.products.length > 1
            ? `<select class="kb-select" data-kb-product="${i}" aria-label="${this.esc(slot.label || 'Choose a piece')}">${slot.products
                .map((p, pi) => `<option value="${pi}"${pi === st.p ? ' selected' : ''}>${this.esc(p.title)}</option>`)
                .join('')}</select>`
            : `<a class="kb-row__title" href="${this.esc(product.url)}">${this.esc(product.title)}</a>`;
        const check = slot.optional
          ? `<label class="kb-check"><input type="checkbox" data-kb-toggle="${i}" checked><span class="kb-check__box" aria-hidden="true"></span><span class="knott-sr">Include ${this.esc(product.title)}</span></label>`
          : `<span class="kb-check kb-check--fixed" aria-hidden="true"><span class="kb-check__box"></span></span>`;
        return `<div class="kb-row" data-kb-row="${i}">
          ${check}
          <a class="kb-row__media" href="${this.esc(product.url)}" tabindex="-1" aria-hidden="true"><img data-kb-img="${i}" alt="" loading="lazy" width="120" height="120"></a>
          <div class="kb-row__main">
            ${slot.label ? `<p class="kb-row__label">${this.esc(slot.label)}</p>` : ''}
            ${slot.locked ? `<p class="kb-row__title kb-row__title--this">This item: ${this.esc(product.title)}</p>` : productSelect}
            <div data-kb-variant-wrap="${i}"></div>
          </div>
          <div class="kb-row__price" data-kb-price="${i}"></div>
        </div>`;
      })
      .join('');
    this.slots.forEach((_, i) => this.renderVariant(i));
  }

  renderVariant(i) {
    const wrap = this.querySelector(`[data-kb-variant-wrap="${i}"]`);
    if (!wrap) return;
    const { product, st } = this.current(i);
    const real = product.variants.filter((v) => v.title);
    if (real.length <= 1) {
      wrap.innerHTML = '';
      return;
    }
    wrap.innerHTML = `<select class="kb-select kb-select--variant" data-kb-variant="${i}" aria-label="Option for ${this.esc(product.title)}">${product.variants
      .map(
        (v) =>
          `<option value="${v.id}"${v.id === st.v ? ' selected' : ''}${v.available ? '' : ' disabled'}>${this.esc(v.title)}${v.available ? '' : ' — sold out'}</option>`
      )
      .join('')}</select>`;
  }

  /** Works out each piece's price the way checkout will. */
  priced() {
    const lines = this.slots.map((slot, i) => {
      const { st, product, variant } = this.current(i);
      return { i, on: st.on, price: variant.price, now: variant.price, tag: '', product, variant, slot };
    });
    const active = lines.filter((l) => l.on);

    if (this.config.rule === 'stack') {
      const sorted = [...active].sort((a, b) => a.price - b.price);
      if (active.length >= 3) {
        sorted[0].now = 0;
        sorted[0].tag = 'Free';
      } else if (active.length === 2 && this.config.pair_percent) {
        const cut = Math.round((sorted[0].price * this.config.pair_percent) / 100);
        sorted[0].now = sorted[0].price - cut;
        sorted[0].tag = `${this.config.pair_percent}% off`;
      }
    } else {
      const anchorOn = !lines[0] || lines[0].on;
      lines.forEach((l) => {
        if (!l.on || !anchorOn) return;
        if (l.slot.percent > 0) {
          const cut = Math.round((l.price * l.slot.percent) / 100);
          l.now = l.price - cut;
          l.tag = l.slot.percent >= 100 ? 'Free' : `${l.slot.percent}% off`;
        } else if (l.slot.amount > 0) {
          l.now = Math.max(0, l.price - l.slot.amount);
          l.tag = `${this.money(l.slot.amount).replace(/\.00$/, '')} off`;
        }
      });
    }
    return { lines, active };
  }

  update() {
    const { lines, active } = this.priced();
    let total = 0;
    let compare = 0;

    lines.forEach((l) => {
      const row = this.querySelector(`[data-kb-row="${l.i}"]`);
      if (row) row.classList.toggle('is-off', !l.on);
      const img = this.querySelector(`[data-kb-img="${l.i}"]`);
      const src = l.variant.image || l.product.image;
      if (img && src && img.getAttribute('src') !== src) img.src = src;
      this.querySelectorAll(`[data-kb-thumb="${l.i}"]`).forEach((t) => {
        if (src && t.getAttribute('src') !== src) {
          t.removeAttribute('srcset');
          t.src = src;
        }
        t.closest('.kb-thumb')?.classList.toggle('is-off', !l.on);
      });
      const price = this.querySelector(`[data-kb-price="${l.i}"]`);
      if (price) {
        price.innerHTML =
          l.now < l.price
            ? `<s>${this.money(l.price)}</s><b>${l.now === 0 ? 'FREE' : this.money(l.now)}</b>${l.tag ? `<span class="kb-tag">${this.esc(l.tag)}</span>` : ''}`
            : `<b>${this.money(l.price)}</b>`;
      }
      if (l.on) {
        total += l.now;
        compare += l.price;
      }
    });

    const saving = compare - total;
    const set = (sel, html) => this.querySelectorAll(sel).forEach((el) => (el.innerHTML = html));
    set('[data-kb-total]', this.money(total));
    set('[data-kb-compare]', saving > 0 ? this.money(compare) : '');
    set('[data-kb-save]', saving > 0 ? `You save ${this.money(saving)}` : '');
    set('[data-kb-save-short]', saving > 0 ? `Save ${this.money(saving).replace(/\.00$/, '')}` : '');
    set('[data-kb-count]', String(active.length));

    const ship = this.config.free_ship || 0;
    if (ship) {
      set(
        '[data-kb-ship]',
        total >= ship
          ? '<span class="kb-ship is-met">✓ Free delivery unlocked</span>'
          : `<span class="kb-ship">${this.money(ship - total)} away from free delivery</span>`
      );
    }

    const addBtn = this.querySelector('[data-kb-add]');
    const label = this.querySelector('[data-kb-add-label]');
    const unavailable = active.some((l) => !l.variant.available);
    if (addBtn) addBtn.disabled = active.length === 0 || unavailable;
    if (label && addBtn?.getAttribute('aria-busy') !== 'true') {
      const base = this.config.cta || 'Add to bag';
      label.textContent = unavailable
        ? 'Pick an available option'
        : `${base.replace(/\[count\]|\{count\}/, String(active.length))} · ${this.money(total)}`;
    }
  }

  onChange = (event) => {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;
    if (el.matches('[data-kb-product]')) {
      const i = Number(el.dataset.kbProduct);
      this.state[i].p = Number(el.value);
      this.state[i].v = this.firstVariant(this.slots[i].products[this.state[i].p]);
      const link = this.querySelector(`[data-kb-row="${i}"] .kb-row__media`);
      if (link) link.setAttribute('href', this.slots[i].products[this.state[i].p].url);
      this.renderVariant(i);
    } else if (el.matches('[data-kb-variant]')) {
      this.state[Number(el.dataset.kbVariant)].v = Number(el.value);
    } else if (el.matches('[data-kb-toggle]')) {
      this.state[Number(el.dataset.kbToggle)].on = el.checked;
    } else {
      return;
    }
    this.update();
  };

  onClick = (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    if (t.closest('[data-kb-open]') && this.dialog) {
      event.preventDefault();
      this.dialog.showModal();
      document.documentElement.classList.add('kb-lock');
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('kb-lock'), { once: true });
      return;
    }
    if (t.closest('[data-kb-close]') && this.dialog) {
      this.dialog.close();
      return;
    }
    if (t === this.dialog) {
      this.dialog.close();
      return;
    }
    if (t.closest('[data-kb-add]')) this.add(t.closest('[data-kb-add]'));
  };

  async add(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const { active } = this.priced();
    if (!active.length) return;
    const error = this.querySelector('[data-kb-error]');
    const label = this.querySelector('[data-kb-add-label]');
    if (error) error.hidden = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding…';

    const items = active.map((l) => {
      const item = { id: l.variant.id, quantity: 1 };
      if (this.config.set_name) item.properties = { Set: this.config.set_name };
      return item;
    });

    try {
      const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add this set.');
      const cart = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());

      if (this.dialog?.open) this.dialog.close();
      button.removeAttribute('aria-busy');
      this.update();
      button.dispatchEvent(
        new CartAddEvent({}, 'knott-bundle', {
          source: 'knott-bundle',
          itemCount: cart.item_count,
          variantId: String(items[0].id),
        })
      );
      if (this.config.after_add === 'cart' || !document.querySelector('cart-drawer-component')) {
        window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
      }
    } catch (e) {
      button.removeAttribute('aria-busy');
      this.update();
      if (error) {
        error.textContent = e instanceof Error ? e.message : 'Could not add this set.';
        error.hidden = false;
      }
    }
  }
}

if (!customElements.get('knott-bundle')) customElements.define('knott-bundle', KnottBundle);

/**
 * <knott-tiers> — product page picker.
 *
 * 1. Colour / size buttons for the product being viewed (rendered in Liquid).
 *    Changing them switches the gallery photo, the price and the ?variant= in the URL.
 * 2. Buy 1  or  Buy 2, get 1 free. With 3 pieces, piece 1 is what was picked above;
 *    pieces 2 and 3 start as the same piece and follow it until the shopper changes
 *    them (labelled Colour / Size menus, or "Change" → picture grid of every piece).
 * The storewide automatic discount makes the lowest-priced of the three free at checkout.
 */
class KnottTiers extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('script[data-kt-config]');
    if (!raw) return;
    try {
      this.config = JSON.parse(raw.textContent || '{}');
    } catch (e) {
      return;
    }
    this.products = (this.config.products || []).filter((p) => p.variants && p.variants.length);
    if (!this.products.length) return;
    this.labels = this.config.labels || {};
    const main = this.products[0];

    // A theme variant picker on the same page takes over: hide ours to avoid two pickers.
    this.pagePicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    this.choose = this.querySelector('[data-kt-choose]');
    if (this.pagePicker && this.choose) {
      this.choose.remove();
      this.choose = null;
    }

    const urlVariant = Number(new URL(window.location.href).searchParams.get('variant'));
    const start =
      main.variants.find((v) => v.id === urlVariant && v.available) ||
      main.variants.find((v) => v.id === this.config.first && v.available) ||
      this.firstVariant(main);
    this.picks = [0, 1, 2].map(() => ({ p: 0, v: start.id, touched: false }));
    this.qty = Number(this.querySelector('[data-kt-qty]:checked')?.value || 1);
    this.sheet = this.querySelector('[data-kt-sheet]');
    this.filter = 'all';

    this.syncChoose();
    this.renderPicks();
    this.update();
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    document.addEventListener('variant:update', this.onPageVariant);
    document.addEventListener('knott:size-pick', this.onSizePick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('variant:update', this.onPageVariant);
    document.removeEventListener('knott:size-pick', this.onSizePick);
  }

  money(cents) {
    return KnottBundle.prototype.money.call({ config: this.config }, cents);
  }

  esc(text) {
    return KnottBundle.prototype.esc.call(this, text);
  }

  firstVariant(product) {
    return product.variants.find((v) => v.id === product.first && v.available) || product.variants.find((v) => v.available) || product.variants[0];
  }

  pick(i) {
    const st = this.picks[i];
    const product = this.products[st.p];
    const variant = product.variants.find((v) => v.id === st.v) || this.firstVariant(product);
    return { product, variant };
  }

  /** Option label shown to shoppers: "Colour" / "Size" for those options, else the admin name. */
  optionLabel(name) {
    const n = String(name || '').toLowerCase();
    if (n.includes('color') || n.includes('colour')) return this.labels.color || 'Colour';
    if (n.includes('size') || n.includes('length')) return this.labels.size || 'Size';
    return name;
  }

  /**
   * The variant with option `index` set to `value`, keeping as many of the other current
   * choices as possible and preferring pieces that are in stock.
   */
  bestVariant(product, current, index, value) {
    const want = [...(current?.options || [])];
    want[index] = value;
    const score = (v) => {
      if (v.options[index] !== value) return -1;
      let s = v.available ? 100 : 0;
      v.options.forEach((o, k) => {
        if (k !== index && o === want[k]) s += 1;
      });
      return s;
    };
    return product.variants.reduce((best, v) => (score(v) > score(best) ? v : best), product.variants[0]);
  }

  /* ---------- 1. Colour / size buttons for this product ---------- */

  /** Reflect piece 1 in the buttons: checked state, "Colour: X" text and sold-out styling. */
  syncChoose() {
    if (!this.choose) return;
    const main = this.products[0];
    const { variant } = this.pick(0);
    this.choose.querySelectorAll('[data-kt-field]').forEach((field) => {
      const o = Number(field.dataset.ktField);
      const valueEl = field.querySelector('[data-kt-field-value]');
      if (valueEl) valueEl.textContent = variant.options[o] || '';
      field.querySelectorAll('[data-kt-opt]').forEach((input) => {
        input.checked = input.value === variant.options[o];
        const inStock = main.variants.some(
          (v) => v.available && v.options[o] === input.value && v.options.every((x, k) => k === o || x === variant.options[k])
        );
        input.closest('.kt-chip')?.classList.toggle('is-out', !inStock);
      });
    });
  }

  /** Piece 1 changed: move the photos, price and URL to it; untouched pieces 2-3 follow. */
  setMain(variantId, { fromPage = false } = {}) {
    const old = this.picks[0].v;
    this.picks.forEach((pk, i) => {
      if (i === 0 || (!pk.touched && pk.p === 0 && pk.v === old)) {
        pk.p = 0;
        pk.v = variantId;
      }
    });
    this.syncChoose();
    this.renderPicks();
    this.update();
    if (fromPage) return;
    const { variant } = this.pick(0);
    this.showMedia(variant.media);
    this.updatePageInfo(variant);
  }

  /** Price, compare-at price and ?variant= for the chosen colour / size. */
  updatePageInfo(variant) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) {}
    const scope = this.closest('.shopify-section') || document;
    scope.querySelectorAll('[class^="knott-hd-price-"]').forEach((el) => (el.textContent = this.money(variant.price)));
    scope.querySelectorAll('[class^="knott-hd-compare-"]').forEach((el) => {
      el.textContent = variant.compare > variant.price ? this.money(variant.compare) : '';
      el.hidden = !(variant.compare > variant.price);
    });
    scope.querySelectorAll('[class^="knott-hd-save-"]').forEach((el) => {
      const on = variant.compare > variant.price;
      el.hidden = !on;
      if (on && /%/.test(el.textContent)) {
        const pct = Math.round(((variant.compare - variant.price) * 100) / variant.compare);
        el.textContent = el.textContent.replace(/\d+\s*%/, `${pct}%`);
      }
    });
  }

  /** Bring the photo of the chosen colour to the front of the product gallery. */
  showMedia(mediaId) {
    if (!mediaId) return;
    const gallery = this.closest('.shopify-section')?.querySelector('media-gallery') || document.querySelector('media-gallery');
    if (!gallery) return;
    const slideshow = gallery.querySelector('slideshow-component');
    const slides = slideshow?.slides || [];
    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    if (index > -1 && index !== slideshow.current) slideshow.select(index);
  }

  /** The theme's own variant picker (if a template has one) changed: follow it. */
  onPageVariant = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;
    const match = this.products[0].variants.find((v) => v.id === variant.id);
    if (!match || this.picks[0].v === match.id) return;
    this.setMain(match.id, { fromPage: true });
  };

  /** "Choose Medium" in the size guide. */
  onSizePick = (event) => {
    const value = event.detail?.value;
    const main = this.products[0];
    if (!value || !main.options?.length) return;
    const index = main.variants[0].options.findIndex((_, o) => main.variants.some((v) => v.options[o] === value));
    if (index < 0) return;
    const next = this.bestVariant(main, this.pick(0).variant, index, value);
    this.setMain(next.id);
    this.choose?.querySelector(`[data-kt-field="${index}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  /* ---------- 2. The pieces in the bundle ---------- */

  /** Labelled menus (one per option) for a piece. */
  optionSelects(i) {
    const { product, variant } = this.pick(i);
    if (!product.options || !product.options.length) return '';
    return product.options
      .map((name, o) => {
        const values = [...new Set(product.variants.map((v) => v.options[o]))];
        if (values.length < 2) return '';
        const options = values
          .map((value) => {
            const out = !product.variants.some((x) => x.available && x.options[o] === value);
            // "Medium (19cm)" → "Medium 19cm" so it fits the narrow menu
            const shown = String(value).replace(/\s*\((\d+(?:[.,]\d+)?)\s*cm\)/i, ' $1cm');
            return `<option value="${this.esc(value)}"${value === variant.options[o] ? ' selected' : ''}${out ? ' disabled' : ''}>${this.esc(out ? `${shown} (sold out)` : shown)}</option>`;
          })
          .join('');
        return `<label class="kt-mini"><span class="kt-mini__label">${this.esc(this.optionLabel(name))}</span><select class="kb-select kt-mini__select" data-kt-pick-opt="${i}:${o}">${options}</select></label>`;
      })
      .join('');
  }

  renderPicks() {
    const box = this.querySelector('[data-kt-picks]');
    if (!box) return;
    const opt = this.querySelector('[data-kt-qty]:checked')?.closest('.kt-opt');
    this.querySelectorAll('.kt-opt.has-picks').forEach((o) => o.classList.remove('has-picks'));

    // Buy 1 with the colour / size buttons above: nothing more to choose.
    if (this.qty === 1 && this.choose) {
      box.innerHTML = '';
      return;
    }

    let html =
      this.qty > 1
        ? `<p class="kt-picks__head"><b>Your ${this.qty} pieces</b><span>Tap “Change” to swap a piece</span></p>`
        : '';
    for (let i = 0; i < this.qty; i++) {
      const { product, variant } = this.pick(i);
      const img = variant.image || product.image;
      const isFirst = i === 0;
      const fixed = isFirst && this.choose;
      const swap = isFirst ? '' : `<button type="button" class="kt-pick__swap" data-kt-swap="${i}" aria-label="Change piece ${i + 1}">Change</button>`;
      const meta = fixed
        ? `<span class="kt-pick__meta">${this.esc(variant.title || '')}${variant.title ? ' · ' : ''}<em>chosen above</em></span>`
        : `<span class="kt-pick__selects">${this.optionSelects(i)}${swap}</span>`;
      html += `<div class="kt-pick" data-kt-row="${i}">
        <span class="kt-pick__img">${img ? `<img src="${this.esc(img)}" alt="" width="44" height="44" loading="lazy">` : ''}<b class="kt-pick__n" aria-hidden="true">${i + 1}</b></span>
        <span class="kt-pick__body">
          <span class="kt-pick__top"><span class="kt-pick__name">${this.esc(product.title)}</span><span class="kt-pick__price" data-kt-price="${i}"></span></span>
          ${meta}
        </span>
      </div>`;
    }
    box.innerHTML = html;
    // Sit the pieces right under the option that is selected.
    if (opt) {
      opt.after(box);
      opt.classList.add('has-picks');
      box.classList.toggle('kt-picks--plain', opt.classList.contains('kt-opt--plain'));
    }
  }

  totals() {
    const chosen = this.picks.slice(0, this.qty).map((_, i) => this.pick(i).variant);
    const was = chosen.reduce((s, v) => s + v.price, 0);
    let now = was;
    let freeIndex = -1;
    if (this.qty >= 3) {
      const min = Math.min(...chosen.map((v) => v.price));
      // On a tie the last piece is the free one ("your 3rd piece is free").
      freeIndex = chosen.map((v) => v.price).lastIndexOf(min);
      now = was - min;
    }
    return { chosen, was, now, freeIndex, ok: chosen.every((v) => v.available) };
  }

  update() {
    const { chosen, was, now, ok, freeIndex } = this.totals();
    this.querySelectorAll('.kt-opt').forEach((o) => {
      const input = o.querySelector('[data-kt-qty]');
      o.classList.toggle('is-on', !!input && input.checked);
    });
    this.querySelectorAll('[data-kt-row]').forEach((row) => {
      const i = Number(row.dataset.ktRow);
      row.classList.toggle('is-free', i === freeIndex);
      const price = row.querySelector('[data-kt-price]');
      if (price && chosen[i]) {
        price.innerHTML = i === freeIndex ? `<s>${this.money(chosen[i].price)}</s><b class="kt-free">FREE</b>` : `<b>${this.money(chosen[i].price)}</b>`;
      }
    });
    const main = this.pick(0).variant;
    const one = this.querySelector('[data-kt-one]');
    if (one) one.textContent = this.money(main.price);
    // The 3-piece card shows this piece × 3 until the shopper mixes in other pieces.
    const now3 = this.querySelector('[data-kt-now]');
    const was3 = this.querySelector('[data-kt-was]');
    const save = this.querySelector('[data-kt-save]');
    const t3 = this.qty >= 3 ? { was, now } : { was: main.price * 3, now: main.price * 2 };
    if (now3) now3.textContent = this.money(t3.now);
    if (was3) was3.textContent = this.money(t3.was);
    if (save) save.textContent = this.money(t3.was - t3.now).replace(/\.00$/, '');

    const label = this.querySelector('[data-kt-label]');
    const btn = this.querySelector('[data-kt-add]');
    if (btn && btn.getAttribute('aria-busy') !== 'true') {
      btn.disabled = !ok;
      const words = this.qty === 1 ? 'Add to bag' : `Add ${this.qty} to bag`;
      if (label) label.textContent = ok ? `${words} · ${this.money(now)}` : 'This option is sold out';
    }
  }

  onChange = (event) => {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;
    if (el.matches('[data-kt-opt]')) {
      const o = Number(el.dataset.ktOpt);
      const next = this.bestVariant(this.products[0], this.pick(0).variant, o, el.value);
      this.setMain(next.id);
      return;
    }
    if (el.matches('[data-kt-qty]')) {
      this.qty = Number(el.value);
      this.renderPicks();
    } else if (el.matches('[data-kt-pick-opt]')) {
      const [i, o] = el.dataset.ktPickOpt.split(':').map(Number);
      const { product, variant } = this.pick(i);
      this.picks[i].v = this.bestVariant(product, variant, o, el.value).id;
      this.picks[i].touched = true;
      if (i === 0) {
        this.setMain(this.picks[0].v);
        return;
      }
      this.renderPicks();
    } else return;
    this.update();
  };

  /* ---------- Picture grid to swap a piece ---------- */

  openSheet(i) {
    if (!this.sheet) return;
    this.sheetFor = i;
    const title = this.sheet.querySelector('[data-kt-sheet-title]');
    if (title) title.textContent = `Choose piece ${i + 1}`;
    this.renderSheet();
    this.sheet.showModal();
    document.documentElement.classList.add('kb-lock');
    this.sheet.addEventListener('close', () => document.documentElement.classList.remove('kb-lock'), { once: true });
  }

  renderSheet() {
    const types = [...new Set(this.products.map((p) => p.type).filter(Boolean))];
    const filters = this.sheet.querySelector('[data-kt-filters]');
    if (filters) {
      filters.innerHTML =
        types.length > 1
          ? ['all', ...types]
              .map(
                (t) =>
                  `<button type="button" class="kt-filter${t === this.filter ? ' is-on' : ''}" data-kt-filter="${this.esc(t)}">${this.esc(t === 'all' ? 'All' : t)}</button>`
              )
              .join('')
          : '';
    }
    const grid = this.sheet.querySelector('[data-kt-grid]');
    if (!grid) return;
    const current = this.picks[this.sheetFor]?.p;
    grid.innerHTML = this.products
      .map((p, pi) => ({ p, pi }))
      .filter(({ p }) => this.filter === 'all' || p.type === this.filter)
      .map(({ p, pi }) => {
        const v = this.firstVariant(p);
        return `<button type="button" class="kt-card${pi === current ? ' is-on' : ''}" data-kt-choose-product="${pi}">
          <span class="kt-card__img">${p.image ? `<img src="${this.esc(p.image)}" alt="" loading="lazy" width="150" height="150">` : ''}</span>
          <span class="kt-card__name">${this.esc(p.title)}</span>
          <span class="kt-card__price">${this.money(v.price)}</span>
        </button>`;
      })
      .join('');
  }

  onClick = async (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;

    const swap = t.closest('[data-kt-swap]');
    if (swap) return this.openSheet(Number(swap.dataset.ktSwap));
    if (this.sheet && (t === this.sheet || t.closest('[data-kt-sheet-close]'))) return this.sheet.close();
    const filter = t.closest('[data-kt-filter]');
    if (filter) {
      this.filter = filter.dataset.ktFilter;
      return this.renderSheet();
    }
    const card = t.closest('[data-kt-choose-product]');
    if (card) {
      const i = this.sheetFor;
      const pi = Number(card.dataset.ktChooseProduct);
      this.picks[i] = { p: pi, v: this.firstVariant(this.products[pi]).id, touched: true };
      this.sheet.close();
      this.renderPicks();
      this.update();
      this.querySelector(`[data-kt-row="${i}"]`)?.classList.add('is-new');
      return;
    }

    const btn = t.closest('[data-kt-add]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    const { chosen } = this.totals();
    const grouped = new Map();
    chosen.forEach((v) => grouped.set(v.id, (grouped.get(v.id) || 0) + 1));
    const items = [...grouped].map(([id, quantity]) => ({ id, quantity }));
    const label = this.querySelector('[data-kt-label]');
    const error = this.querySelector('[data-kt-error]');
    if (error) error.hidden = true;
    btn.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding…';
    const root = window.Shopify?.routes?.root || '/';
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag.');
      const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      btn.removeAttribute('aria-busy');
      this.update();
      btn.dispatchEvent(
        new CartAddEvent({}, 'knott-tiers', { source: 'knott-tiers', itemCount: cart.item_count, variantId: String(items[0].id) })
      );
      if (!document.querySelector('cart-drawer-component')) window.location.href = root + 'cart';
    } catch (e) {
      btn.removeAttribute('aria-busy');
      this.update();
      if (error) {
        error.textContent = e instanceof Error ? e.message : 'Could not add to bag.';
        error.hidden = false;
      }
    }
  };
}

if (!customElements.get('knott-tiers')) customElements.define('knott-tiers', KnottTiers);
